import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function UserManagement() {
  const [users, setUsers]         = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({ username:'', full_name:'', password:'', role:'user' });
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [sortKey, setSortKey]     = useState('full_name');
  const [sortDir, setSortDir]     = useState('asc');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser]     = useState(null);
  const [editForm, setEditForm]           = useState({ username:'', full_name:'', role:'user', password:'' });
  const [editLoading, setEditLoading]     = useState(false);
  const [editError, setEditError]         = useState('');

  const { isAdmin } = useAuth();
  const navigate    = useNavigate();

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    loadUsers();
  }, []);

  const loadUsers = () =>
    api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const roleConfig = {
    admin:        { badge:'badge-critical', label:'Admin',        desc:'Full access — all tickets, user management, dropdown manager' },
    user:         { badge:'badge-open',     label:'User',         desc:'Create tickets, edit own tickets (ATS consultant match)' },
    sales_manager:{ badge:'badge-resolved', label:'Sales Manager',desc:'View only — sees tickets where they are the Sales Manager' },
    fullviewer:   { badge:'badge-progress', label:'Full Viewer',  desc:'View only — sees all tickets and dashboard, no edit' },
  };

  const sortedUsers = [...users].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];

    if (sortKey === 'role') {
      av = roleConfig[av]?.label || av;
      bv = roleConfig[bv]?.label || bv;
    }
    if (sortKey === 'is_active') {
      av = av ? 1 : 0; bv = bv ? 1 : 0;
    }
    if (sortKey === 'created_at') {
      av = new Date(av).getTime(); bv = new Date(bv).getTime();
    }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();

    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const createUser = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.post('/users', form);
      setSuccess(`User "${form.username}" created`);
      setShowModal(false);
      setForm({ username:'', full_name:'', password:'', role:'user' });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally { setLoading(false); }
  };

  const toggleUser = async id => {
    await api.put(`/users/${id}/toggle`);
    loadUsers();
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setEditError('');
    setShowEditModal(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true); setEditError('');
    try {
      await api.put(`/users/${editingUser.id}`, editForm);
      setSuccess(`User "${editForm.username}" updated`);
      setShowEditModal(false);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update user');
    } finally { setEditLoading(false); }
  };

  return (
    <Layout title="User Management">
      {success && <div className="alert alert-success">{success}</div>}

      {/* Role legend */}
      <div className="card" style={{padding:'14px 18px',marginBottom:16}}>
        <div style={{fontSize:12,fontWeight:600,color:'#888',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.05em'}}>Role Permissions</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10}}>
          {Object.entries(roleConfig).map(([role,{badge,label,desc}]) => (
            <div key={role} style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <span className={`badge ${badge}`} style={{marginTop:1,flexShrink:0}}>{label}</span>
              <span style={{fontSize:12,color:'#666'}}>{desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary"
          onClick={() => { setShowModal(true); setError(''); setSuccess(''); }}>
          <i className="fa-solid fa-user-plus"></i> Add User
        </button>
      </div>

      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {[
                  { key:'full_name',  label:'Full Name' },
                  { key:'username',   label:'Username'  },
                  { key:'role',       label:'Role'      },
                  { key:'is_active',  label:'Status'    },
                  { key:'created_at', label:'Created'   },
                ].map(col => (
                  <th key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{cursor:'pointer', userSelect:'none', whiteSpace:'nowrap'}}>
                    {col.label}
                    <i className={`fa-solid ${
                      sortKey !== col.key ? 'fa-sort'
                      : sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                    }`} style={{marginLeft:6, fontSize:11, color: sortKey===col.key ? '#6382e6' : '#ccc'}}></i>
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map(u => (
                <tr key={u.id}>
                  <td style={{fontWeight:500}}>{u.full_name}</td>
                  <td style={{fontFamily:'monospace',fontSize:13,color:'#6382e6'}}>{u.username}</td>
                  <td>
                    <span className={`badge ${roleConfig[u.role]?.badge||'badge-open'}`}>
                      {roleConfig[u.role]?.label || u.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.is_active?'badge-resolved':'badge-closed'}`}>
                      {u.is_active?'Active':'Disabled'}
                    </span>
                  </td>
                  <td style={{fontSize:12,color:'#888'}}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{display:'flex',gap:6}}>
                      <button className="btn btn-sm btn-secondary"
                        onClick={() => openEditModal(u)}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className={`btn btn-sm ${u.is_active?'btn-danger':'btn-secondary'}`}
                        onClick={() => toggleUser(u.id)}>
                        <i className={`fa-solid ${u.is_active?'fa-ban':'fa-circle-check'}`}></i>
                        {u.is_active ? ' Disable' : ' Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-backdrop"
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title"><i className="fa-solid fa-user-plus"></i> Add New User</div>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={createUser}>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div className="field">
                  <label>Full Name <span className="req">*</span></label>
                  <input type="text" value={form.full_name}
                    onChange={e=>setForm({...form,full_name:e.target.value})} required />
                </div>
                <div className="field">
                  <label>Username <span className="req">*</span></label>
                  <input type="text" value={form.username}
                    onChange={e=>setForm({...form,username:e.target.value})} required />
                </div>
                <div className="field">
                  <label>Password <span className="req">*</span></label>
                  <input type="password" value={form.password}
                    onChange={e=>setForm({...form,password:e.target.value})}
                    required minLength={6} />
                  <span className="hint">Minimum 6 characters</span>
                </div>
                <div className="field">
                  <label>Role</label>
                  <select value={form.role}
                    onChange={e=>setForm({...form,role:e.target.value})}>
                    <option value="user">User — create &amp; edit own tickets</option>
                    <option value="sales_manager">Sales Manager — view own tickets only</option>
                    <option value="fullviewer">Full Viewer — view all tickets, no edit</option>
                    <option value="admin">Admin — full access</option>
                  </select>
                </div>
                {/* Hint for sales_manager role */}
                {form.role === 'sales_manager' && (
                  <div style={{background:'#FAEEDA',border:'1px solid #EF9F27',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#854F0B'}}>
                    <i className="fa-solid fa-circle-info" style={{marginRight:6}}></i>
                    Full Name must exactly match the name in the <strong>Sales Managers</strong> dropdown table.
                  </div>
                )}
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:20}}>
                <button type="button" className="btn btn-secondary"
                  onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading
                    ? <><i className="fa-solid fa-spinner fa-spin"></i> Creating...</>
                    : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User modal */}
      {showEditModal && editingUser && (
        <div className="modal-backdrop"
          onClick={e => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="modal">
            <div className="modal-title"><i className="fa-solid fa-user-pen"></i> Edit User</div>
            {editError && <div className="alert alert-error">{editError}</div>}
            <form onSubmit={saveEdit}>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div className="field">
                  <label>Full Name <span className="req">*</span></label>
                  <input type="text" value={editForm.full_name}
                    onChange={e=>setEditForm({...editForm,full_name:e.target.value})} required />
                </div>
                <div className="field">
                  <label>Username <span className="req">*</span></label>
                  <input type="text" value={editForm.username}
                    onChange={e=>setEditForm({...editForm,username:e.target.value})} required />
                </div>
                <div className="field">
                  <label>Role</label>
                  <select value={editForm.role}
                    onChange={e=>setEditForm({...editForm,role:e.target.value})}>
                    <option value="user">User — create &amp; edit own tickets</option>
                    <option value="sales_manager">Sales Manager — view own tickets only</option>
                    <option value="fullviewer">Full Viewer — view all tickets, no edit</option>
                    <option value="admin">Admin — full access</option>
                  </select>
                </div>
                {editForm.role === 'sales_manager' && (
                  <div style={{background:'#FAEEDA',border:'1px solid #EF9F27',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#854F0B'}}>
                    <i className="fa-solid fa-circle-info" style={{marginRight:6}}></i>
                    Full Name must exactly match the name in the <strong>Sales Managers</strong> dropdown table.
                  </div>
                )}
                <div className="field">
                  <label>New Password</label>
                  <input type="password" value={editForm.password}
                    onChange={e=>setEditForm({...editForm,password:e.target.value})}
                    placeholder="Leave blank to keep current password"
                    minLength={6} />
                  <span className="hint">Only fill this in if you want to reset the password</span>
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:20}}>
                <button type="button" className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading
                    ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</>
                    : <><i className="fa-solid fa-check"></i> Save Changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}